// MEGSZEMÉLYESÍTÉS-FAL a FELÜLETEN: a mögé zárt vezérlők tiltottak, amíg más nevében dolgozunk.
//
// MIÉRT KELL, HA A SZERVER MÁR FALAZ: az Odin a hitelesítés-kezelő self-végpontokat `@DenyImpersonation()`-nel
// zárja (jelszó · 2FA · avatar · Asana-PAT · profil-PATCH), és megszemélyesítő tokennel `403
// impersonation_fenced`-et ad — tehát a TILALOM a szerveren áll, és ez így helyes. A felület viszont eddig
// engedte KITÖLTENI az űrlapot, és csak a mentésnél derült ki, hogy nem megy: a támogató beírta a jelszót,
// megkapta a hibát, és nem tudta, miért (user-lelet). A fal így KÉT rétegű: a szerver a
// biztonsági kapu, ez pedig az IGAZSÁG kimondása — a vezérlő ne ígérjen olyat, ami nem teljesíthető.
//
// A MECHANIZMUS `fieldset[disabled]`: natívan tiltja MINDEN benne lévő form-vezérlőt (input, select, button),
// tehát nem kell per-vezérlő `disabled`-plumbing, és egy ÚJ vezérlő is automatikusan tiltott lesz. A
// `pointer-events-none` a nem-form elemekre (pl. egy link vagy egy kártya-fej kattintó) zár, az `opacity` a
// vizuális jelzés. A MAGYARÁZAT szövegesen is ott van: a halványítás önmagában nem mondja meg, miért.
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/components/ui/text';
import { useImpersonation } from '@/features/impersonation/use-impersonation';
import { cn } from '@/lib/utils';

export interface ImpersonationFenceProps {
  children: ReactNode;
  /**
   * Írja-e ki a MAGYARÁZATOT a tartalom fölé. Alapból IGEN: a néma tiltás rosszabb, mint a felesleges sor.
   *
   * `false` ott, ahol UGYANAZON A LAPON már áll a magyarázat egy fölöttes szekcióban — a profil-lapon négy
   * kártya van a fal mögött (adatok · jelszó · 2FA · avatar), és négyszer ugyanaz a mondat zaj volt
   * (user-döntés: „ezt nem kell minden szekciónál kiírni, elég az alapadatok felett").
   */
  hint?: boolean;
  className?: string | undefined;
}

export function ImpersonationFence({
  children,
  hint = true,
  className,
}: ImpersonationFenceProps): JSX.Element {
  const { t } = useTranslation();
  const { active } = useImpersonation();

  // NEM megszemélyesített állapotban SEMMILYEN extra elem nem kerül a DOM-ba (se `fieldset`, se jelzés):
  // a hétköznapi eset geometriája és stílusa így bitre változatlan.
  if (!active) return <>{children}</>;

  return (
    <div className={cn('space-y-2', className)}>
      {hint && (
        <div className="text-danger-700 dark:text-danger-300 flex items-start gap-2">
          {/* A LAKAT a szöveg ELSŐ SORÁNAK optikai középvonalán áll. MÉRVE (2026-09-09, user-lelet: „nincs
              egy vonalban"): a magyarázat 12px/16px, az első sor-box 15px — 14px ikon + 1px felső köz
              esetén a két középvonal PONTOSAN egybeesik; a korábbi 16px + 2px 2 pixellel lejjebb ült.
              `items-start` + fix köz kell (nem `items-center`): a szöveg szűk sávban két sorra tördel, és
              akkor a középre igazítás a sorok KÖZÉ csúsztatná az ikont. */}
          <LockClosedIcon className="mt-px size-3.5 shrink-0" aria-hidden="true" />
          {/* `role="note"`: a magyarázat a képernyőolvasónak is elérhető — a halványítás önmagában nem
            információ. A tiltás tényét a `fieldset[disabled]` amúgy is közli minden vezérlőn. */}
          <Text variant="secondary" as="p" role="note" className="text-inherit">
            {t('impersonation.fencedHint')}
          </Text>
        </div>
      )}
      <fieldset disabled className="pointer-events-none min-w-0 opacity-55">
        {children}
      </fieldset>
    </div>
  );
}
